<template>
	<div>
		<div class="box">
			<div class="row align-items-center mb-3">
				<div class="col">
					<h3 class="mb-0">{{ $t('pax.title_export') }}</h3>
				</div>
				<div class="col-auto">
					<b-button
						variant="primary"
						:disabled="generating"
						@click="startGeneration"
					>
						<font-awesome-icon :icon="['fal', 'file-spreadsheet']" class="mr-2"/>
						{{ $t('pax.generate_export') }}
					</b-button>
				</div>
			</div>

			<!-- Progression -->
			<div v-if="generating" class="mb-3">
				<p class="mb-1">
					<span v-if="step === 'counting'">{{ $t('pax.export_counting') }}</span>
					<span v-else-if="step === 'pages'">{{ $t('pax.export_generating', { current: currentPage + 1, total: totalPages }) }}</span>
					<span v-else-if="step === 'compiling'">{{ $t('pax.export_compiling') }}</span>
				</p>
				<b-progress
					:value="progressValue"
					:max="100"
					show-progress
					animated
					variant="primary"
				/>
			</div>

			<!-- Erreur -->
			<b-alert v-if="errorMsg" variant="danger" show class="mb-3">
				{{ errorMsg }}
			</b-alert>
		</div>

		<!-- Liste des exports -->
		<div class="box mt-3">
			<div class="row align-items-center mb-3">
				<div class="col">
					<h4 class="mb-0">{{ $t('pax.exports_history') }}</h4>
				</div>
				<div class="col-auto">
					<b-button variant="outline-secondary" size="sm" @click="loadList">
						<font-awesome-icon :icon="['fal', 'sync']" />
					</b-button>
				</div>
			</div>

			<LoadingSpinner v-if="loadingList" />

			<div v-else-if="exports.length === 0" class="text-muted">
				{{ $t('pax.no_export') }}
			</div>

			<b-list-group v-else flush>
				<b-list-group-item
					v-for="item in exports"
					:key="item.filename"
					class="d-flex justify-content-between align-items-center"
				>
					<span>
						<font-awesome-icon :icon="['fal', 'file-spreadsheet']" class="mr-2 text-success"/>
						{{ item.date }}
					</span>
					<b-button
						variant="outline-primary"
						size="sm"
						:disabled="item.downloading"
						@click="downloadExport(item)"
					>
						<font-awesome-icon :icon="['fal', 'download']" class="mr-1"/>
						{{ $t('pax.download') }}
					</b-button>
				</b-list-group-item>
			</b-list-group>
		</div>
	</div>
</template>

<script type="text/javascript">
	import PaxMixin from '@/mixins/Pax'

	export default {
		name: "PAXExport",
		mixins: [PaxMixin],
		data() {
			return {
				generating: false,
				step: null,
				currentPage: 0,
				totalPages: 0,
				loadingList: false,
				exports: [],
				errorMsg: null,
			}
		},
		computed: {
			progressValue() {
				if (this.step === 'counting') return 2
				if (this.step === 'compiling') return 98
				if (this.totalPages === 0) return 0
				return Math.round(((this.currentPage + 1) / this.totalPages) * 95)
			}
		},
		mounted() {
			this.loadList()
		},
		methods: {
			async loadList() {
				this.loadingList = true
				const list = await this.loadExportList() || []
				this.exports = list.map(item => ({ ...item, downloading: false }))
				this.loadingList = false
			},

			async downloadExport(item) {
				item.downloading = true
				try {
					const blob = await this.downloadPaxExport(item.filename)
					if (!blob) throw new Error('Erreur téléchargement')
					const blobUrl = URL.createObjectURL(blob)
					const a = document.createElement('a')
					a.href = blobUrl
					a.download = item.filename
					document.body.appendChild(a)
					a.click()
					document.body.removeChild(a)
					URL.revokeObjectURL(blobUrl)
				} finally {
					item.downloading = false
				}
			},

			async startGeneration() {
				this.generating = true
				this.errorMsg = null
				this.step = 'counting'

				try {
					// 1. Compter
					const count = await this.loadExportCount()
					if (!count) throw new Error('Impossible de compter les juments')

					this.totalPages = count.pages
					const datetimeKey = this._buildDatetimeKey()

					// 2. Générer chaque page
					this.step = 'pages'
					for (let i = 0; i < this.totalPages; i++) {
						this.currentPage = i
						const res = await this.generateExportPage(datetimeKey, i)
						if (!res) throw new Error(`Erreur lors de la génération de la page ${i + 1}`)
					}

					// 3. Compiler
					this.step = 'compiling'
					const compiled = await this.compileExport(datetimeKey, this.totalPages)
					if (!compiled) throw new Error('Erreur lors de la compilation')

					// 4. Rafraîchir la liste
					await this.loadList()

				} catch (e) {
					this.errorMsg = e.message || this.$t('pax.export_error')
				} finally {
					this.generating = false
					this.step = null
				}
			},

			_buildDatetimeKey() {
				const now = new Date()
				const pad = n => String(n).padStart(2, '0')
				return String(now.getFullYear()) +
					pad(now.getMonth() + 1) +
					pad(now.getDate()) +
					pad(now.getHours()) +
					pad(now.getMinutes())
			}
		},
		components: {
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
		}
	}
</script>
